import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import PageSEO from '@/components/feature/PageSEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === Number(id));

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20 pb-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <i className="ri-error-warning-line text-5xl text-foreground-400 mb-4 block"></i>
            <h1 className="text-xl font-bold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-foreground-600 mb-4">您访问的文章不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex px-6 py-2.5 rounded-full bg-primary-500 hover:bg-primary-600 text-white font-medium text-sm transition-colors cursor-pointer whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishTime,
    publisher: {
      '@type': 'Organization',
      name: '番茄视频',
    },
    url: `https://www.fanqieshipin.com/article/${article.id}`,
  };

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: article.category },
  ];

  return (
    <>
      <PageSEO
        title={article.title}
        description={article.excerpt}
        keywords={`${article.tags.join(',')},番茄视频,影视资讯`}
        canonicalUrl={`/article/${article.id}`}
        ogType="article"
        schemaData={[articleSchema]}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <article className="max-w-[900px] mx-auto px-4 md:px-6 pt-6 pb-12">
          <Breadcrumb items={breadcrumbItems} />

          <div className="mb-6">
            <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-700 mb-4">
              {article.category}
            </span>
            <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i> {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i> {article.publishTime}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i> {article.readCount} 阅读
              </span>
            </div>
          </div>

          <div className="rounded-xl overflow-hidden mb-8 bg-background-200">
            <img
              src={article.cover}
              alt={article.title}
              className="w-full aspect-video object-cover"
              title={article.title}
            />
          </div>

          <div
            className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed
              prose-h2:text-xl prose-h2:font-bold prose-h2:text-foreground-900 prose-h2:mt-8 prose-h2:mb-4
              prose-p:text-foreground-700 prose-p:leading-loose prose-p:mb-4
              prose-strong:text-foreground-900"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-10 pt-6 border-t border-background-200">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-foreground-500">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-700 cursor-pointer hover:bg-secondary-200 transition-colors whitespace-nowrap"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>

          <div className="mt-8 flex items-center justify-between flex-wrap gap-3">
            <Link
              to="/"
              className="text-sm text-primary-600 hover:text-primary-700 transition-colors flex items-center gap-1 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i> 返回首页
            </Link>
            <div className="flex items-center gap-3">
              <span className="text-sm text-foreground-500">分享到：</span>
              <button className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer" aria-label="分享到微信">
                <i className="ri-wechat-line"></i>
              </button>
              <button className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer" aria-label="分享到微博">
                <i className="ri-weibo-line"></i>
              </button>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}